import React, { useState } from 'react';

const toRupees = (paise) => {
  if (!paise && paise !== 0) return '₹0.00';
  return `₹${(paise / 100).toFixed(2)}`;
};

const LedgerTableCompact = ({ ledgerData, customerData }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'TransTime', direction: 'asc' });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes}`;
  };

  const exportToExcel = () => {
    if (ledgerData.length === 0) return;

    const customerName = customerData?.name || 'Customer';
    const customerMobile = customerData?.phone_number || 'NA';
    const filename = `Wallet Ledger_${customerName}_${customerMobile}.xlsx`;

    // Prepare CSV data
    const headers = ['Date & Time', 'Type', 'Action By', 'Opening (Rs)', 'Amount (Rs)', 'Closing (Rs)', 'D/C', 'Tags'];

    const rows = sortedData.map(t => [
      formatDate(t.TransTime),
      t.TransType?.replace('WalletComponents::', '') || 'N/A',
      t.ActionBy || 'N/A',
      t.Opening != null ? (t.Opening / 100).toFixed(2) : '0.00',
      t.Amount != null ? (t.Amount / 100).toFixed(2) : '0.00',
      t.Closing != null ? (t.Closing / 100).toFixed(2) : '0.00',
      t.AcctTransType === 'CREDIT' ? 'CR' : 'DR',
      [
        t.IsloyltyCashback ? 'Cashback' : '',
        t.IsBulkupload ? 'Bulk' : ''
      ].filter(Boolean).join(', ') || 'N/A'
    ]);

    // Create CSV content
    let csvContent = 'Wallet Ledger Report\n';
    csvContent += `Customer Name: ${customerName}\n`;
    csvContent += `Mobile Number: ${customerMobile}\n`;
    csvContent += `Total Transactions: ${ledgerData.length}\n`;
    csvContent += `Export Date: ${new Date().toLocaleString()}\n\n`;

    csvContent += headers.join(',') + '\n';
    rows.forEach(row => {
      csvContent += row.map(cell => `"${cell}"`).join(',') + '\n';
    });

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename.replace('.xlsx', '.csv'));
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const sortedData = [...ledgerData].sort((a, b) => {
    if (sortConfig.key === 'TransTime') {
      const dateA = new Date(a.TransTime);
      const dateB = new Date(b.TransTime);
      return sortConfig.direction === 'asc' ? dateA - dateB : dateB - dateA;
    }
    return 0;
  });

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc',
    });
  };

  const containerStyle = {
    backgroundColor: '#ffffff',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
  };

  const headerStyle = {
    backgroundColor: '#f3f4f6',
    borderBottom: '1px solid #d1d5db',
    padding: '8px',
    position: 'sticky',
    top: 0,
    zIndex: 10,
  };

  const headerTitleStyle = {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#1f2937',
    margin: '0 0 4px 0',
  };

  const headerSubtitleStyle = {
    fontSize: '12px',
    color: '#4b5563',
    margin: 0,
  };

  const tableBodyStyle = {
    overflowY: 'auto',
    flex: 1,
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '12px',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
  };

  const theadStyle = {
    backgroundColor: '#f3f4f6',
    position: 'sticky',
    top: 0,
    zIndex: 10,
  };

  const thStyle = {
    padding: '8px',
    textAlign: 'left',
    fontWeight: '600',
    color: '#374151',
    whiteSpace: 'nowrap',
    borderRight: '1px solid #d1d5db',
    fontSize: '12px',
    cursor: 'pointer',
    userSelect: 'none',
    backgroundColor: '#f3f4f6',
  };

  const thRightStyle = {
    ...thStyle,
    textAlign: 'right',
  };

  const thCenterStyle = {
    ...thStyle,
    textAlign: 'center',
  };

  const tdStyle = {
    padding: '8px',
    borderRight: '1px solid #e5e7eb',
    fontSize: '12px',
  };

  const tdRightStyle = {
    ...tdStyle,
    textAlign: 'right',
  };

  const tdCenterStyle = {
    ...tdStyle,
    textAlign: 'center',
  };

  const badgeStyle = (bgColor, textColor) => ({
    display: 'inline-block',
    backgroundColor: bgColor,
    color: textColor,
    padding: '2px 6px',
    borderRadius: '3px',
    fontSize: '11px',
    fontWeight: '500',
  });

  const badgeCreditStyle = badgeStyle('#dbeafe', '#1e40af');
  const badgeDebitStyle = badgeStyle('#dcfce7', '#166534');
  const badgeUserStyle = badgeStyle('#dcfce7', '#166534');
  const badgeMerchantStyle = badgeStyle('#e9d5ff', '#6b21a8');
  const badgeCRStyle = badgeStyle('#dcfce7', '#166534');
  const badgeDRStyle = badgeStyle('#fee2e2', '#991b1b');
  const badgeCashbackStyle = badgeStyle('#fef3c7', '#b45309');
  const badgeBulkStyle = badgeStyle('#fed7aa', '#92400e');

  const rowStyle = (index) => ({
    borderBottom: '1px solid #e5e7eb',
    backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb',
    height: '36px',
  });

  const rowHoverStyle = {
    backgroundColor: '#eff6ff',
  };

  const footerStyle = {
    backgroundColor: '#f3f4f6',
    borderTop: '1px solid #d1d5db',
    padding: '8px',
    fontSize: '12px',
    color: '#374151',
  };

  const headerContentStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '16px',
  };

  const headerLeftStyle = {
    flex: 1,
  };

  const exportButtonStyle = {
    padding: '6px 12px',
    backgroundColor: '#10b981',
    color: '#ffffff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    transition: 'background-color 0.2s',
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <div style={headerContentStyle}>
          <div style={headerLeftStyle}>
            <h3 style={headerTitleStyle}>Wallet Transactions</h3>
            <p style={headerSubtitleStyle}>Total: {ledgerData.length} transactions</p>
          </div>
          <button
            onClick={exportToExcel}
            style={exportButtonStyle}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#059669'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#10b981'}
            title="Export ledger to CSV file"
          >
            📥 Export
          </button>
        </div>
      </div>

      {/* Scrollable Table */}
      <div style={tableBodyStyle}>
        <table style={tableStyle}>
          <thead style={theadStyle}>
            <tr style={{ borderBottom: '1px solid #d1d5db' }}>
              <th style={thStyle} onClick={() => handleSort('TransTime')}>
                Date & Time {sortConfig.key === 'TransTime' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th style={thStyle}>Type</th>
              <th style={thStyle}>Action By</th>
              <th style={thRightStyle}>Opening (₹)</th>
              <th style={thRightStyle}>Amount (₹)</th>
              <th style={thRightStyle}>Closing (₹)</th>
              <th style={thCenterStyle}>D/C</th>
              <th style={thCenterStyle}>Tags</th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((transaction, index) => (
              <tr
                key={index}
                style={rowStyle(index)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#eff6ff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#f9fafb';
                }}
              >
                {/* Date & Time */}
                <td style={{ ...tdStyle, fontWeight: '500' }}>
                  {formatDate(transaction.TransTime)}
                </td>

                {/* Transaction Type */}
                <td style={tdStyle}>
                  <span style={badgeCreditStyle}>
                    {transaction.TransType?.replace('WalletComponents::', '') || 'N/A'}
                  </span>
                </td>

                {/* Action By */}
                <td style={tdStyle}>
                  <span style={transaction.ActionBy === 'User' ? badgeUserStyle : badgeMerchantStyle}>
                    {transaction.ActionBy || 'N/A'}
                  </span>
                </td>

                {/* Opening Balance */}
                <td style={{ ...tdRightStyle, fontWeight: '500' }}>
                  {toRupees(transaction.Opening)}
                </td>

                {/* Amount */}
                <td style={{
                  ...tdRightStyle,
                  fontWeight: 'bold',
                  color: transaction.AcctTransType === 'CREDIT' ? '#16a34a' : '#dc2626'
                }}>
                  {toRupees(transaction.Amount)}
                </td>

                {/* Closing Balance */}
                <td style={{ ...tdRightStyle, fontWeight: 'bold' }}>
                  {toRupees(transaction.Closing)}
                </td>

                {/* Debit/Credit */}
                <td style={tdCenterStyle}>
                  <span style={transaction.AcctTransType === 'CREDIT' ? badgeCRStyle : badgeDRStyle}>
                    {transaction.AcctTransType === 'CREDIT' ? 'CR' : 'DR'}
                  </span>
                </td>

                {/* Tags */}
                <td style={tdCenterStyle}>
                  <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', flexWrap: 'wrap' }}>
                    {transaction.IsloyltyCashback && (
                      <span style={badgeCashbackStyle}>
                        Cashback
                      </span>
                    )}
                    {transaction.IsBulkupload && (
                      <span style={badgeBulkStyle}>
                        Bulk
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer Summary */}
      <div style={footerStyle}>
        <p style={{ margin: 0 }}>
          <strong>Summary:</strong> Total Transactions: {ledgerData.length}
        </p>
      </div>
    </div>
  );
};

export default LedgerTableCompact;
